import { useQuery } from '@tanstack/react-query';
import { Logo } from '@/components/brand/logo';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Check } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ThemeToggle } from '@/components/theme-toggle';
import { ErrorState, Skeleton } from '@/components/ui';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { ConnectGoogleStep } from './connect-google-step';
import { ImportProgress } from './import-progress';
import { ImportReview } from './import-review';
import { LanguageStep } from './language-step';
import { AssistantSetup } from '../assistant/assistant-setup';
import { setupApi, type SetupStage } from './setup-api';
import { SignupChat } from './signup-chat';
import { SignupStep } from './signup-step';

/**
 * הצטרפות: שפה → הרשמה → חיבור מקורות (Google / חשבונית ירוקה) → סריקה → אישור (ADR 0002, 0004).
 *
 * השלב נקבע בשרת (`setupStage`), לא ב-state מקומי: הבעלים יוצא ל-Google
 * וחוזר, סוגר טאב באמצע סריקה, או נכנס מחר — ותמיד נוחת במקום הנכון.
 */
export function OnboardingWizard() {
  const { t } = useTranslation(['onboarding', 'common', 'assistant']);
  // שלב השפה — מקומי, רק לפני ההרשמה. טופס ההרשמה נשאר מורכב (מוסתר) כדי
  // שחזרה לשלב השפה לא תמחק את מה שכבר הוקלד.
  const [languageDone, setLanguageDone] = useState(false);
  const { status, can } = useAuth();
  const navigate = useNavigate();
  const isOwner = can('OWNER');

  const setup = useQuery({
    queryKey: ['onboarding-import', 'status'],
    queryFn: ({ signal }) => setupApi.status(signal),
    enabled: status === 'authenticated' && isOwner,
    // בזמן סריקה — polling. בשאר השלבים המסך משתנה רק מפעולה של הבעלים.
    refetchInterval: (q) => (q.state.data?.stage === 'IMPORTING' ? 2_000 : false),
  });

  const stage = setup.data?.stage;
  useEffect(() => {
    // מחובר שאינו בעלים, או עסק שכבר סיים — אין פה מה לעשות.
    if ((status === 'authenticated' && !isOwner) || stage === 'DONE') {
      void navigate({ to: '/', replace: true });
    }
  }, [status, isOwner, stage, navigate]);

  // אחרי ההרשמה, ההקמה היא שיחה עם העוזר (ברירת המחדל). `?manual=1` — האשף.
  // סריקה שכבר רצה או ממתינה לאישור נשארת באשף: שם המסך שלה.
  const { manual } = useSearch({ strict: false });
  const stage0 = setup.data?.stage;
  if (status === 'authenticated' && isOwner && setup.data && !manual && (stage0 === 'SIGNED_UP' || stage0 === 'GOOGLE_CONNECTED')) {
    return <AssistantSetup />;
  }

  // לפני ההרשמה — שיחה (שפה, שם העסק, השם שלך, ואז אימייל וסיסמה בכרטיס).
  // `?manual=1` — הטופס הישן, לתמיכה.
  if (status === 'anonymous' && !manual) return <SignupChat />;

  let body: ReactNode;
  let step = 0;
  if (status === 'loading' || (status === 'authenticated' && setup.isPending)) {
    body = <Skeleton className="h-72" />;
  } else if (status === 'anonymous') {
    step = languageDone ? 1 : 0;
    body = (
      <>
        {languageDone ? null : <LanguageStep onContinue={() => setLanguageDone(true)} />}
        <div hidden={!languageDone}>
          <SignupStep onBack={() => setLanguageDone(false)} />
        </div>
      </>
    );
  } else if (setup.isError) {
    body = <ErrorState error={setup.error} onRetry={() => void setup.refetch()} />;
  } else if (setup.data) {
    step = stepIndex(setup.data.stage);
    body =
      setup.data.stage === 'IMPORTING' ? (
        <ImportProgress status={setup.data} />
      ) : setup.data.stage === 'REVIEW' ? (
        <ImportReview />
      ) : (
        <ConnectGoogleStep status={setup.data} />
      );
  }

  return (
    <div className="min-h-dvh bg-canvas">
      <header className="mx-auto flex max-w-3xl items-center justify-between px-4 py-5">
        <div className="flex items-center gap-2.5">
          <Logo />
        </div>
        <ThemeToggle />
      </header>

      <main className="mx-auto max-w-3xl px-4 pb-16">
        <Stepper current={step} />
        <div className="mt-6">{body}</div>
        {status === 'authenticated' && manual && (stage === 'SIGNED_UP' || stage === 'GOOGLE_CONNECTED') ? (
          <p className="mt-6 text-center text-xs">
            <Link to="/onboarding" search={{}} className="font-medium text-accent hover:underline">
              {t('assistant:setup.backToChat')}
            </Link>
          </p>
        ) : null}
        {status === 'anonymous' ? (
          <p className="mt-6 text-center text-xs text-fg-muted">
            {t('wizard.alreadyRegistered')}{' '}
            <Link to="/login" className="font-medium text-accent hover:underline">
              {t('wizard.login')}
            </Link>
          </p>
        ) : null}
      </main>
    </div>
  );
}

const STEPS = ['language', 'business', 'sources', 'learning', 'approve'] as const;

function stepIndex(stage: SetupStage): number {
  switch (stage) {
    case 'SIGNED_UP':
    case 'GOOGLE_CONNECTED':
      return 2;
    case 'IMPORTING':
      return 3;
    case 'REVIEW':
    case 'DONE':
      return 4;
  }
}

function Stepper({ current }: { current: number }) {
  const { t } = useTranslation('onboarding');
  return (
    <ol className="flex items-center gap-2" aria-label={t('wizard.stepsAria')}>
      {STEPS.map((key, i) => (
        <li
          key={key}
          className="flex flex-1 items-center gap-2"
          aria-current={i === current ? 'step' : undefined}
        >
          <span
            className={cn(
              'grid size-6 shrink-0 place-items-center rounded-full border text-2xs font-semibold',
              i < current && 'border-accent bg-accent text-fg-on-accent',
              i === current && 'border-accent text-accent',
              i > current && 'border-border text-fg-subtle',
            )}
          >
            {i < current ? <Check className="size-3.5" aria-hidden /> : i + 1}
          </span>
          <span
            className={cn(
              'hidden text-xs sm:inline',
              i === current ? 'font-medium text-fg' : 'text-fg-muted',
            )}
          >
            {t(`wizard.steps.${key}`)}
          </span>
          {i < STEPS.length - 1 ? <span className="h-px flex-1 bg-border" aria-hidden /> : null}
        </li>
      ))}
    </ol>
  );
}
